import { ApiClientProvider, PermissionsProvider, usePermissions } from '@hetivo/hetivo-lib-front-utils';
import type { PermissionsContextValue } from '@hetivo/hetivo-lib-front-utils';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './auth';
import { DefaultErrorPage, DefaultNotFoundPage, DefaultPendingPage } from '@hetivo/hetivo-lib-web-utils';
import type { DefaultErrorPageProps } from '@hetivo/hetivo-lib-web-utils';
import { env } from './config';
import { useTenantModules } from './hooks/use-tenant-modules';
import type { TenantModulesResult } from './hooks/use-tenant-modules';
import { apiConfigs, apiLogger, CORRELATION_ID_PREFIX, setTokenProvider, tokenProvider } from './lib/api-client';
import { queryClient } from './lib/query-client';
import { routeTree } from './routeTree.gen';

function AppDefaultErrorPage({ error }: DefaultErrorPageProps): React.JSX.Element {
  return <DefaultErrorPage error={error} isDev={env.isDev} />;
}

/** Mounted under this path; shared by the router and the public-route check below. */
const BASEPATH = '/frontoffice';

/**
 * The public magic-link page — the one route a person with no Hetivo account ever loads.
 *
 * Kept as a plain string rather than derived from the route tree because it is needed BEFORE the
 * router mounts, to decide which provider tree to build at all.
 */
const PUBLIC_MAGIC_LINK_PREFIX = `${BASEPATH}/r/`;

function createAppRouter(): ReturnType<typeof createRouter> {
  return createRouter({
    basepath: BASEPATH,
    // Initial context — will be updated by InnerApp with real auth/permissions state.
    // Cast is intentional: TanStack Router requires a context value at creation time,
    // but the real objects are provided via RouterProvider before any route renders.
    context: {
      auth: undefined as unknown as ReturnType<typeof useAuth>,
      entitlement: undefined as unknown as TenantModulesResult,
      permissions: undefined as unknown as PermissionsContextValue,
    },
    defaultErrorComponent: AppDefaultErrorPage,
    defaultNotFoundComponent: DefaultNotFoundPage,
    defaultPendingComponent: DefaultPendingPage,
    routeTree,
  });
}

const router = createAppRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    /** i18n key (under the `documentTitle` namespace) for this page's browser tab title. */
    titleKey?: string;
    /**
     * Marks a subtree whose title is written by the page itself (e.g. an entity
     * detail page showing the loaded record's name). The root layout writes no
     * title for these and defers to the page. See `useRouteDocumentTitle`.
     */
    dynamic?: boolean;
  }
}

/** Bridges auth context token and activity notification into the API client */
function AuthApiClientBridge({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { getToken, notifyActivity, signOut } = useAuth();

  useEffect(() => {
    setTokenProvider(getToken);
  }, [getToken]);

  return (
    <ApiClientProvider
      configs={apiConfigs}
      correlationIdPrefix={CORRELATION_ID_PREFIX}
      logger={apiLogger}
      onRequestSent={notifyActivity}
      onUnauthorized={signOut}
      tokenProvider={tokenProvider}
    >
      {children}
    </ApiClientProvider>
  );
}

/**
 * Thin bridge between the app-specific auth context and the library's
 * PermissionsProvider. We keep this wrapper (rather than calling useAuth()
 * inside the library) so that hetivo-lib-front-utils stays decoupled from
 * any particular auth implementation.
 */
function AuthenticatedPermissionsProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated } = useAuth();
  return <PermissionsProvider isAuthenticated={isAuthenticated}>{children}</PermissionsProvider>;
}

/** Inner component that has access to auth and permissions contexts and updates router context */
function InnerApp(): React.JSX.Element {
  const auth = useAuth();
  const permissions = usePermissions();
  const entitlement = useTenantModules();

  return <RouterProvider context={{ auth, entitlement, permissions }} router={router} />;
}

/**
 * The magic-link page, mounted WITHOUT the auth stack.
 *
 * `AuthProvider` boots Firebase, refreshes the ID token, exchanges it for a Hetivo JWT and then
 * `PermissionsProvider` fetches `/users/me/permissions` — four network calls, before anything is
 * rendered. On `/r/<token>` every one of them is wasted and none is wanted: the reader has no
 * account, and the page's whole credential is the token in its own URL.
 *
 * Skipping them is not only about speed. A public page is the one page any stranger can reach, and
 * it has no business holding a signed-in visitor's session in memory while they read it. Combined
 * with the anonymous api client (see `features/magic-link/api/anonymous-client.ts`), a magic link
 * now touches no part of the authenticated application.
 *
 * The router context is stubbed for the same reason the initial context in `createAppRouter` is:
 * nothing on this path reads it. `__root` only TYPES `auth`/`permissions`, and `/r/$token` declares
 * no `beforeLoad`. If a future public route needs either, it must supply its own — not reach for
 * this stub.
 *
 * There is deliberately no in-app navigation off this page, so the app cannot end up rendering an
 * authenticated route inside this tree.
 */
function PublicApp(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider
        context={{
          auth: undefined as unknown as ReturnType<typeof useAuth>,
          entitlement: undefined as unknown as TenantModulesResult,
          permissions: undefined as unknown as PermissionsContextValue,
        }}
        router={router}
      />
    </QueryClientProvider>
  );
}

function AuthenticatedApp(): React.JSX.Element {
  return (
    <AuthProvider>
      <AuthApiClientBridge>
        <QueryClientProvider client={queryClient}>
          <AuthenticatedPermissionsProvider>
            <InnerApp />
          </AuthenticatedPermissionsProvider>
        </QueryClientProvider>
      </AuthApiClientBridge>
    </AuthProvider>
  );
}

/**
 * Decided once, from the URL this document was loaded with, rather than reactively: which provider
 * tree to build is a mount-time question, and re-deciding it mid-session would tear down and
 * rebuild the entire app.
 */
function App(): React.JSX.Element {
  const isPublicMagicLink = window.location.pathname.startsWith(PUBLIC_MAGIC_LINK_PREFIX);

  return isPublicMagicLink ? <PublicApp /> : <AuthenticatedApp />;
}

export default App;
